import React, { useContext, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { css, Global } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { ReservationType } from '@/models/reservation'
import { ApprovedStatusType } from '@/models/reservation-approved-status'
import i18n from '@/i18n'
import { SelectRelevantModal } from '../select-relevant-modal'
import { SelectCreationModal } from './select-creation-modal'
import { useErrorHandler } from '@/hooks/use-error-handler'
import * as api from '@/apis/aipass'
import { LoaderContextCreator } from '@/contexts/loader'
import { Popover } from 'antd'
import { ChangeReservationApprovedStatusModal } from '@/components/molecules/change-reservation-approved-status-modal'
import { ApprovedStatusButton } from '@/components/molecules/approved-status-button'
import { RoomNumberPopup } from '@/components/molecules/room-number-popup'
import { ReservationCopyModal } from '@/components/organisms/accommodation-management/reservation-copy-modal'
import { ReservationSubMenu } from '@/components/pages/accommodation-management/reservation/reservation-list/reservation-sub-menu'

type Props = {
  reservations: ReservationType[]
  checkinReception?: () => void
  currentTabReservationId: string | undefined
  setCurrentTabReservationId: (val: string | undefined) => void
  frontSupportedForCheckin: boolean
  checkinId: string
  onReload: () => void
}

export const approvedStatus = [
  {
    value: ApprovedStatusType.Checkin,
    label: i18n.t('Checkin'),
  },
  {
    value: ApprovedStatusType.Stay,
    label: i18n.t('During your stay'),
  },
  {
    value: ApprovedStatusType.Checkout,
    label: i18n.t('Checkout'),
  },
]

export const ReservationList: React.FC<Props> = props => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const [isOpenBulkUpdateApprovedStatusPopup, setIsOpenBulkUpdateApprovedStatusPopup] = useState<boolean>(false)
  const [openChangeApprovedStatusPopup, setOpenChangeApprovedStatusPopup] = useState<{ [key: string]: boolean }>({})
  const [openListSubMenu, setOpenListSubMenu] = useState<{ [key: string]: boolean }>({})
  const [isSelectCreationModalOpen, setIsSelectCreationModalOpen] = useState<boolean>(false)
  const [isSearchModalOpen, setIsSearchModalOpen] = useState<boolean>(false)
  const [frontSupported, setFrontSupported] = useState(false)
  const [isReservationCopyModalOpen, setIsReservationCopyModalOpen] = useState<boolean>(false)
  const [selectedReservationForCopy, setSelectedReservationForCopy] = useState<ReservationType | undefined>()
  const [reservationNoticeMessage, setReservationNoticeMessage] = useState<{ [key: string]: string }>({})
  const onDeleteReservation = async (checkinId, reservationId) => {
    if (!window.confirm(t('Untethered reservation. When released, the reservation is moved to the reservation status.'))) {
      return
    }

    try {
      setIsLoading(true)
      await api.deleteAdminReservationCheckin({ checkinId, reservationId })
      const otherFirstReservation = props.reservations?.find(r => r.reservationId !== reservationId)?.reservationId
      props.setCurrentTabReservationId(otherFirstReservation)
      props.onReload()
      // Clear Advance Receipt Flag
      api.deleteSalesAdvancePayment(reservationId)
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  const onChangeCheckinGuidance = (reservationId: string, memo: string) => {
    api.updateReservationCheckinGuidance({ reservationId, memo }).catch(error => errorHandler(error))
  }

  const onChangeFrontSupported = async () => {
    try {
      setFrontSupported(!frontSupported)
      await api.updateAdminCheckinList({
        checkinId: props.checkinId,
        mode: 'frontSupported',
        frontSupported: Number(!frontSupported),
      })
    } catch (error) {
      setFrontSupported(frontSupported)
      errorHandler(error)
    }
  }

  const hasSalesPaid = async (reservationId?: string) => {
    const res = await api.fetchSales(props.checkinId)
    if (reservationId) {
      return res.sales.some(item => item.isPaid && item.isConfirm && item.reservationId === reservationId)
    } else {
      return res.sales.some(item => item.isPaid && item.isConfirm)
    }
  }

  const onBulkChangeApprovedStatus = async (approvedStatus: ApprovedStatusType) => {
    try {
      setIsOpenBulkUpdateApprovedStatusPopup(false)
      const hasCheckedInReservation = props.reservations.some(
        r => r.approvedStatus === ApprovedStatusType.Stay || r.approvedStatus === ApprovedStatusType.Checkout,
      )
      if ((await hasSalesPaid()) && hasCheckedInReservation && approvedStatus === ApprovedStatusType.Checkin) {
        alert('計上済の科目があるので、ステータスを変更できません。')
        return
      }
      setIsLoading(true)
      const allReservationIds = props.reservations.map(r => r.reservationId)
      await api.putAdminReservationApprovedStatus({ reservationIds: allReservationIds, approvedStatus })
      props.onReload()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const reservationSubMenu = (reservation: ReservationType) => {
    const frontSupportedEvent =
      reservation.approvedStatus === ApprovedStatusType.Stay
        ? {
            value: frontSupported,
            onChange: onChangeFrontSupported,
          }
        : undefined

    const noticeMessageEvent =
      reservation.approvedStatus === ApprovedStatusType.Checkin
        ? {
            value: reservationNoticeMessage[reservation.reservationId] || '',
            onChange: (v: string) => setReservationNoticeMessage(prev => ({ ...prev, [reservation.reservationId]: v })),
            onComplete: (memo: string) => onChangeCheckinGuidance(reservation.reservationId, memo),
          }
        : undefined
    return (
      <ReservationSubMenu
        onCopyReservation={() => {
          setOpenListSubMenu({ ...openListSubMenu, [reservation.reservationId]: false })
          setSelectedReservationForCopy(reservation)
          setIsReservationCopyModalOpen(true)
        }}
        visible={openListSubMenu[reservation.reservationId]}
        onUnlink={() => {
          setOpenListSubMenu({ ...openListSubMenu, [reservation.reservationId]: false })
          onDeleteReservation(reservation.checkinId, reservation.reservationId)
        }}
        frontSupported={frontSupportedEvent}
        noticeMessage={noticeMessageEvent}
      />
    )
  }

  const bulkUpdateApprovedStatusPopup = () => (
    <ul>
      <li css={dropDownContentStyle} onClick={() => onBulkChangeApprovedStatus(ApprovedStatusType.Checkin)}>
        <p css={dropDownTitleStyle}>{t('Checkin')}</p>
      </li>
      <li css={dropDownContentStyle} onClick={() => onBulkChangeApprovedStatus(ApprovedStatusType.Stay)}>
        <p css={dropDownTitleStyle}>{t('During your stay')}</p>
      </li>
      <li css={dropDownContentStyle} onClick={() => onBulkChangeApprovedStatus(ApprovedStatusType.Checkout)}>
        <p css={dropDownTitleStyle}>{t('Checkout')}</p>
      </li>
    </ul>
  )

  const computeMultiListStyle = (reservationId: string) => {
    if (props.reservations.length <= 1) {
      return
    }
    return css({
      background: reservationId === props.currentTabReservationId ? '#c2e7ff !important' : undefined,
      '&:hover': { background: '#f2f2f2' },
    })
  }

  useEffect(() => {
    setFrontSupported(props.frontSupportedForCheckin || false)
  }, [props.frontSupportedForCheckin])

  useEffect(() => {
    setReservationNoticeMessage(
      Object.fromEntries(props.reservations.map(({ reservationId, checkinGuidanceMemo }) => [reservationId, checkinGuidanceMemo || ''])),
    )
  }, [props.reservations])

  return (
    <>
      <Global
        styles={css({
          '.ant-popover-inner-content': { padding: 'unset' },
          '.ant-popover-placement-bottomRight': { paddingTop: 0 },
          '.ant-popover-arrow': { display: 'none' },
        })}
      />
      <div css={reservationListContainer}>
        <div css={reservationListSubject}>
          <div css={reservationListTitleStyle}>
            <p>{t('Reservation list')}</p>
            <p
              style={{ display: 'flex', alignItems: 'center', color: '#F2A40B', fontSize: 12, gap: 7, cursor: 'pointer' }}
              onClick={() => setIsSelectCreationModalOpen(true)}
            >
              <img src={require('@/static/images/link_add_orange.svg')} alt="" />
              {t('Add Reservations')}
            </p>
          </div>

          <Popover
            placement="bottomRight"
            trigger="click"
            content={bulkUpdateApprovedStatusPopup()}
            visible={isOpenBulkUpdateApprovedStatusPopup}
            onVisibleChange={visible => {
              setIsOpenBulkUpdateApprovedStatusPopup(visible)
            }}
          >
            <div css={reservationButtonStyle}>
              {t('Bulk change')}
              <img css={arrowStyle} src={require('@/static/images/arrow_yellow.svg')} alt="" />
            </div>
          </Popover>
        </div>
        <div css={tableScrollContainer}>
          <table style={{ width: '100%' }}>
            <thead>
              <tr css={listTitle}>
                <th css={reservationDateStyle}>{t('Date of stay')}</th>
                <th css={reservationUserStyle}>{t('Guest')}</th>
                <th css={roomNumberStyle}>{t('Room number')}</th>
                <th css={amountCancelledStyle}>{t('Amount to be collected')}</th>
                <th css={reservationStatusStyle}>{t('Status')}</th>
                <th css={updatedStyle}>{t('Update date')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {!props.reservations.length && (
                <tr css={listStyle}>
                  <td css={reservationDateStyle}>-</td>
                  <td css={reservationUserStyle}>-</td>
                  <td css={roomNumberStyle}>-</td>
                  <td css={amountCancelledStyle}>¥0</td>
                  <td onClick={props.checkinReception}>
                    <div css={approvedStatusCheckinReceptionStyle}>{t('Check-in reception')}</div>
                  </td>
                  <td css={updatedStyle}>
                    <p>{t('Stay')}：-</p>
                    <p>{t('Out')}：-</p>
                  </td>
                  <td style={{ width: '5%' }}></td>
                </tr>
              )}
              {props.reservations.map((reservation, key) => (
                <tr
                  key={reservation.reservationId}
                  css={[listStyle, computeMultiListStyle(reservation.reservationId)]}
                  onClick={() => props.setCurrentTabReservationId(reservation.reservationId)}
                >
                  <td css={reservationDateStyle}>
                    {`${dayjs(reservation.checkinDate).format(t('MM-DD')) ? dayjs(reservation.checkinDate).format(t('MM-DD')) : ''} ${
                      reservation.nights ?? 0
                    }${t('Night')}`}
                  </td>
                  <td css={reservationUserStyle}>{reservation.guestName}</td>
                  <td css={roomNumberStyle}>
                    <RoomNumberPopup rooms={reservation.assignedRoom || []} />
                  </td>
                  <td css={[amountCancelledStyle, { color: reservation?.unpaid ? '#F2A40B' : '' }]}>
                    ¥{Number(reservation?.unpaid || 0).toLocaleString()}
                  </td>
                  <td css={reservationStatusStyle}>
                    <ApprovedStatusButton
                      currentStatus={reservation.approvedStatus}
                      onClick={() => {
                        setOpenChangeApprovedStatusPopup({ ...openChangeApprovedStatusPopup, [reservation.reservationId]: true })
                      }}
                    />
                    {openChangeApprovedStatusPopup[reservation.reservationId] && (
                      <ChangeReservationApprovedStatusModal
                        defaultStatus={reservation.approvedStatus}
                        defaultRoom={reservation.assignedRoom
                          .filter(r => r.assignStatus === reservation.approvedStatus)
                          .map(r => r.guestRoomAssignId)}
                        reservationId={reservation.reservationId}
                        rooms={reservation.assignedRoom}
                        onClose={() =>
                          setOpenChangeApprovedStatusPopup({ ...openChangeApprovedStatusPopup, [reservation.reservationId]: false })
                        }
                        onChange={props.onReload}
                      />
                    )}
                  </td>
                  <td css={updatedStyle}>
                    <p>
                      {t('Stay')}：{reservation?.statusStayAt ? dayjs(reservation.statusStayAt).format(t('MM-DD HH:mm')) : '-'}
                    </p>
                    <p>
                      {t('Out')}：{reservation?.statusCheckoutAt ? dayjs(reservation.statusCheckoutAt).format(t('MM-DD HH:mm')) : '-'}
                    </p>
                  </td>
                  <td style={{ width: '5%' }}>
                    <Popover
                      placement="bottomRight"
                      trigger="click"
                      content={reservationSubMenu(reservation)}
                      visible={openListSubMenu[reservation.reservationId]}
                      onVisibleChange={visible => {
                        setOpenListSubMenu({ ...openListSubMenu, [reservation.reservationId]: visible })
                      }}
                    >
                      <div
                        css={css({
                          borderRadius: '50px',
                          width: '30px',
                          height: '30px',
                          cursor: 'pointer',
                          position: 'relative',
                          ':hover': { backgroundColor: '#f2f2f2' },
                        })}
                      >
                        <img src={require('@/static/images/icon_other.svg')} />
                      </div>
                    </Popover>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {isSelectCreationModalOpen && (
        <SelectCreationModal
          onClose={() => setIsSelectCreationModalOpen(false)}
          onSelectSearchRelevant={() => {
            setIsSearchModalOpen(true)
            setIsSelectCreationModalOpen(false)
          }}
        />
      )}
      {isSearchModalOpen && (
        <SelectRelevantModal
          reservation={props.reservations.find(r => r.reservationId === props.currentTabReservationId)}
          onClose={() => setIsSearchModalOpen(false)}
        />
      )}
      {isReservationCopyModalOpen && selectedReservationForCopy && (
        <ReservationCopyModal setIsModalOpen={setIsReservationCopyModalOpen} reservation={selectedReservationForCopy} />
      )}
    </>
  )
}

const reservationListContainer = css({
  backgroundColor: '#fff',
  border: '1px solid #CCCCCC',
  borderRadius: '10px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
})

const tableScrollContainer = css({
  overflowY: 'auto',
  overflowX: 'auto',
  maxHeight: '400px',
  flex: 1,
  minHeight: 0,
})

const listTitle = css({
  minWidth: 'max-content',
  color: '#676767',
  fontWeight: 'bold',
  fontSize: 12,
  height: 36,
  'th, td': {
    paddingBlock: 10,
    paddingLeft: 32,
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    ':first-of-type': {
      '@media(max-width: 1080px)': {
        paddingLeft: 16,
      },
    },
    ':last-of-type': {
      paddingRight: 32,
      '@media(max-width: 1080px)': {
        paddingRight: 16,
      },
    },
  },
  th: {
    backgroundColor: '#f5f5f5',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
})

const listStyle = css(listTitle, {
  color: '#272727',
  fontWeight: 'normal',
  fontSize: 14,
  lineHeight: '24px',
  height: 56,
  cursor: 'pointer',
  borderTop: '1px solid #F2F2F2',
  '&:first-of-type': { borderTop: 'none' },
})

const reservationListSubject = css({
  fontSize: 14,
  fontWeight: 'bold',
  padding: '13px 34px 8px 32px',
  display: 'flex',
  justifyContent: 'space-between',
  '@media(max-width: 1080px)': {
    paddingInline: 16,
  },
})

const reservationListTitleStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 27,
  margin: 'auto 0px',
})

const reservationDateStyle = css({ width: '12%' })
const reservationUserStyle = css({ width: '20%' })
const roomNumberStyle = css({
  width: '20%',
  '@media(max-width: 1080px)': {
    width: '9%',
    div: { maxWidth: 90 },
  },
})
const reservationStatusStyle = css({ width: '11%' })
const amountCancelledStyle = css({ width: '10%' })
const updatedStyle = css({
  width: '20%',
  p: { lineHeight: 1.2 },
})

const reservationStatusSelectboxStyle = css({
  width: 116,
  fontSize: 12,
  fontWeight: 'bold',
  textAlign: 'center',
  letterSpacing: 0,
  borderRadius: 5,
  color: '#fff',
  position: 'relative',
  paddingRight: 6,
  cursor: 'pointer',
})

const arrowStyle = css({
  position: 'absolute',
  right: 0,
  padding: '4px 8px',
})

const reservationButtonStyle = css({
  width: 116,
  border: '1px solid #E7A73C',
  borderRadius: 5,
  color: '#E7A73C',
  fontSize: 12,
  fontWeight: 'bold',
  textAlign: 'center',
  padding: '6px 0',
  position: 'relative',
  paddingRight: 10,
})

const approvedStatusCheckinReceptionStyle = css(reservationStatusSelectboxStyle, {
  backgroundColor: '#F2A40B',
})

const dropDownContentStyle = css({
  display: 'flex',
  height: 34,
  listStyle: 'none',
  alignItems: 'center',
  padding: '0 16px',
  cursor: 'pointer',
  '&:hover': { background: '#f2f2f2' },
})

const dropDownTitleStyle = css({
  fontSize: 14,
  fontWeight: 'normal',
  color: '#272727',
})
